// src/services/notificationService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  getDocs
} from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';

class NotificationService {
  
  // Create notification for provider
  async createProviderNotification(providerId, notificationData) {
    try {
      const notification = {
        ...notificationData,
        providerId,
        read: false,
        responded: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        expiresAt: notificationData.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        
        // Notification metadata
        priority: notificationData.priority || 'normal', // 'high', 'normal', 'low'
        category: notificationData.category || 'booking_request',
        actionRequired: notificationData.actionRequired || true
      };

      const docRef = await addDoc(collection(db, 'pb-notifications'), notification);
      return { id: docRef.id, ...notification };
    } catch (error) {
      console.error('Error creating provider notification:', error);
      throw error;
    }
  }

  // Get provider notifications with real-time listener
  subscribeToProviderNotifications(providerId, callback, options = {}) {
    try {
      const {
        limit: queryLimit = 50,
        includeRead = true,
        categories = null
      } = options;

      let q = query(
        collection(db, 'pb-notifications'),
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );

      if (!includeRead) {
        q = query(q, where('read', '==', false));
      }

      if (categories && categories.length > 0) {
        q = query(q, where('category', 'in', categories));
      }

      if (queryLimit) {
        q = query(q, limit(queryLimit));
      }

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const notifications = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
        }));

        callback(notifications);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error subscribing to provider notifications:', error);
      return () => {}; // Return empty function to prevent errors
    }
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const notificationRef = doc(db, 'pb-notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Mark notification as responded
  async markAsResponded(notificationId, responseData = {}) {
    try {
      const notificationRef = doc(db, 'pb-notifications', notificationId);
      await updateDoc(notificationRef, {
        responded: true,
        respondedAt: serverTimestamp(),
        responseData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as responded:', error);
      throw error;
    }
  }

  // Bulk mark notifications as read
  async markMultipleAsRead(notificationIds) {
    try {
      const batch = writeBatch(db);
      
      notificationIds.forEach(notificationId => {
        const notificationRef = doc(db, 'pb-notifications', notificationId);
        batch.update(notificationRef, {
          read: true,
          readAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error bulk marking notifications as read:', error);
      throw error;
    }
  }

  // Delete notification
  async deleteNotification(notificationId) {
    try {
      await deleteDoc(doc(db, 'pb-notifications', notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  // Get notification stats for provider
  async getProviderNotificationStats(providerId) {
    try {
      const q = query(
        collection(db, 'pb-notifications'),
        where('providerId', '==', providerId)
      );

      const snapshot = await getDocs(q);
      const notifications = snapshot.docs.map(doc => doc.data());

      return {
        total: notifications.length,
        unread: notifications.filter(n => !n.read).length,
        unresponded: notifications.filter(n => !n.responded && n.actionRequired).length,
        byCategory: notifications.reduce((acc, n) => {
          acc[n.category] = (acc[n.category] || 0) + 1;
          return acc;
        }, {}),
        byPriority: notifications.reduce((acc, n) => {
          acc[n.priority] = (acc[n.priority] || 0) + 1;
          return acc;
        }, {})
      };
    } catch (error) {
      console.error('Error getting notification stats:', error);
      return { total: 0, unread: 0, unresponded: 0, byCategory: {}, byPriority: {} };
    }
  }

  // Clean up expired notifications
  async cleanExpiredNotifications() {
    try {
      const q = query(
        collection(db, 'pb-notifications'),
        where('expiresAt', '<=', new Date().toISOString())
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      return snapshot.docs.length;
    } catch (error) {
      console.error('Error cleaning expired notifications:', error);
      return 0;
    }
  }

  async sendContactAddedNotification(bookId, contact, workspaceName = null) {
    if (!contact.email) return null;
    
    try {
      const sendNotification = httpsCallable(functions, 'sendDiaryNotification');
      const result = await sendNotification({
        type: 'contact_added',
        recipientEmail: contact.email,
        recipientName: contact.name,
        bookId,
        workspaceName
      });
      return result.data;
    } catch (error) {
      console.error('Failed to send contact notification:', error);
      return null;
    }
  }
  /**
   * Send workspace member added notification
   */
  async sendMemberAddedNotification(bookId, member, workspace) {
    if (!member.email) return null;
    
    try {
      const sendNotification = httpsCallable(functions, 'sendDiaryNotification');
      const result = await sendNotification({
        type: 'workspace_member_added',
        recipientEmail: member.email,
        recipientName: member.displayName || member.email,
        bookId,
        workspaceId: workspace.id,
        workspaceName: workspace.name,
        workspaceType: workspace.type,
        role: member.role
      });
      return result.data;
    } catch (error) {
      console.error('Failed to send member notification:', error);
      return null;
    }
  }
}

export const notificationService = new NotificationService();